import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LocalAudioCache from '../utils/localAudioCache';

const FALLBACK_API_URL = 'https://taraanaya-prod-production.up.railway.app';
const API_URL = process.env.REACT_APP_BACKEND_URL || FALLBACK_API_URL;

// Planet icons
const PLANET_ICONS = {
  Sun: '☉', Moon: '☽', Mars: '♂', Mercury: '☿', Jupiter: '♃',
  Venus: '♀', Saturn: '♄', Rahu: '☊', Ketu: '☋'
};

// Planet colors
const PLANET_COLORS = {
  Sun: '#FFD700', Moon: '#C8C8FF', Mars: '#DC143C', Mercury: '#32CD32',
  Jupiter: '#FFA500', Venus: '#FF69B4', Saturn: '#8B4513', Rahu: '#4B0082', Ketu: '#006400'
};

// Astro elements for orbiting icons
const PLANET_ASTRO_ELEMENTS = {
  Sun: [
    { symbol: '☉', label: 'Surya', color: '#FFD700', size: 'xlarge' },
    { symbol: '♡', label: 'Heart', color: '#FF6347', size: 'large' },
    { symbol: '👁', label: 'Eyes', color: '#FFD700', size: 'medium' },
    { symbol: '△', label: 'Spine', color: '#FFA500', size: 'small' }
  ],
  Moon: [
    { symbol: '☽', label: 'Chandra', color: '#C8C8FF', size: 'xlarge' },
    { symbol: '◎', label: 'Mind', color: '#9370DB', size: 'large' },
    { symbol: '♡', label: 'Emotion', color: '#C8C8FF', size: 'medium' },
    { symbol: '◈', label: 'Stomach', color: '#4169E1', size: 'small' }
  ],
  Mars: [
    { symbol: '♂', label: 'Mangal', color: '#DC143C', size: 'xlarge' },
    { symbol: '⚠', label: 'Injury', color: '#FF4500', size: 'large' },
    { symbol: '◈', label: 'Head', color: '#DC143C', size: 'medium' },
    { symbol: '✂', label: 'Surgery', color: '#FF6347', size: 'small' }
  ],
  Mercury: [
    { symbol: '☿', label: 'Budh', color: '#32CD32', size: 'xlarge' },
    { symbol: '◬', label: 'Nerves', color: '#4682B4', size: 'large' },
    { symbol: '◎', label: 'Speech', color: '#32CD32', size: 'medium' },
    { symbol: '◈', label: 'Skin', color: '#A9A9A9', size: 'small' }
  ],
  Jupiter: [
    { symbol: '♃', label: 'Guru', color: '#FFA500', size: 'xlarge' },
    { symbol: '◉', label: 'Immunity', color: '#32CD32', size: 'large' },
    { symbol: '◈', label: 'Liver', color: '#FFA500', size: 'medium' },
    { symbol: '△', label: 'Growth', color: '#FFD700', size: 'small' }
  ],
  Venus: [
    { symbol: '♀', label: 'Shukra', color: '#FF69B4', size: 'xlarge' },
    { symbol: '◎', label: 'Kidney', color: '#FF1493', size: 'large' },
    { symbol: '♡', label: 'Beauty', color: '#FF69B4', size: 'medium' },
    { symbol: '◈', label: 'Repro', color: '#FFB6C1', size: 'small' }
  ],
  Saturn: [
    { symbol: '♄', label: 'Shani', color: '#8B4513', size: 'xlarge' },
    { symbol: '◬', label: 'Bones', color: '#A0522D', size: 'large' },
    { symbol: '◎', label: 'Chronic', color: '#8B4513', size: 'medium' },
    { symbol: '⏳', label: 'Slow', color: '#CD853F', size: 'small' }
  ],
  Rahu: [
    { symbol: '☊', label: 'Rahu', color: '#4B0082', size: 'xlarge' },
    { symbol: '◎', label: 'Hidden', color: '#800080', size: 'large' },
    { symbol: '?', label: 'Mystery', color: '#9400D3', size: 'medium' },
    { symbol: '◈', label: 'Toxic', color: '#4B0082', size: 'small' }
  ],
  Ketu: [
    { symbol: '☋', label: 'Ketu', color: '#006400', size: 'xlarge' },
    { symbol: '✨', label: 'Spirit', color: '#32CD32', size: 'large' },
    { symbol: '✂', label: 'Surgery', color: '#DC143C', size: 'medium' },
    { symbol: '◬', label: 'Nerves', color: '#228B22', size: 'small' }
  ]
};

const FutureStoryRevolutionary = () => {
  const navigate = useNavigate();
  
  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isIOSWebView = isIOS && (window.webkit?.messageHandlers || window.Capacitor);
  
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Loading your cosmic future...');
  const [audioLoadProgress, setAudioLoadProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState('ready'); // ready, intro, timeline, complete
  const [selectedYear, setSelectedYear] = useState(-1);
  const [selectedLanguage, setSelectedLanguage] = useState('hinglish');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [currentNarrationText, setCurrentNarrationText] = useState('');
  const [displayedText, setDisplayedText] = useState(''); // Text revealed so far
  
  const [yearlyPredictions, setYearlyPredictions] = useState([]);
  const [personName, setPersonName] = useState('');
  const [fullNarration, setFullNarration] = useState({ hinglish: '', english: '', hindi: '' });
  const [introText, setIntroText] = useState('');
  const [timelineNarrations, setTimelineNarrations] = useState([]);
  
  const audioRef = useRef(null);
  const audioCache = useRef({ intro: {}, timeline: {} });
  const isPlayingRef = useRef(false);
  const textRevealIntervalRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // Dynamic text reveal - reveals text sentence by sentence in sync with audio
  const startTextReveal = (fullText, durationMs) => {
    // Clear any existing interval
    if (textRevealIntervalRef.current) {
      clearInterval(textRevealIntervalRef.current);
    }
    
    setDisplayedText('');
    
    // Split text into sentences
    const sentences = fullText.split(/(?<=[।.!?])\s*/).filter(s => s.trim());
    if (sentences.length === 0) {
      setDisplayedText(fullText);
      return;
    }
    
    const timePerSentence = durationMs / sentences.length;
    let currentSentenceIndex = 0;
    
    // Show first sentence immediately
    setDisplayedText(sentences[0]);
    currentSentenceIndex = 1;
    
    textRevealIntervalRef.current = setInterval(() => {
      if (currentSentenceIndex < sentences.length && isPlayingRef.current) {
        setDisplayedText(prev => prev + ' ' + sentences[currentSentenceIndex]);
        currentSentenceIndex++;
      } else {
        clearInterval(textRevealIntervalRef.current);
        textRevealIntervalRef.current = null;
        // Show full text at end
        setDisplayedText(fullText);
      }
    }, timePerSentence);
  };

  // Stop text reveal
  const stopTextReveal = () => {
    if (textRevealIntervalRef.current) {
      clearInterval(textRevealIntervalRef.current);
      textRevealIntervalRef.current = null;
    }
  };

  // Parse and split narration into intro and timeline parts
  const parseNarration = (narration, timeline) => {
    const fullText = narration?.hinglish || '';
    
    // Find where timeline starts - matches backend marker for FUTURE
    const timelineMarker = 'Ab aapki future life timeline dekhte hain:';
    const timelineIndex = fullText.indexOf(timelineMarker);
    
    let intro = '';
    let timelineTexts = [];
    
    if (timelineIndex > -1) {
      intro = fullText.substring(0, timelineIndex + timelineMarker.length);
      const timelinePart = fullText.substring(timelineIndex + timelineMarker.length);
      
      // Split by Mahadasha markers - format is "Dasha Mahadasha (years):"
      timeline.forEach((t, idx) => {
        const dashaName = t.dasha_lord;
        const years = t.years || '';
        
        // Try to find this Mahadasha's text in the narration
        // Pattern: "DashaName Mahadasha (years): ... until next Mahadasha or end"
        const startPattern = new RegExp(`${dashaName} Mahadasha \\(${years.replace('-', '[-–]')}\\):`);
        const startMatch = timelinePart.match(startPattern);
        
        if (startMatch) {
          const startIdx = timelinePart.indexOf(startMatch[0]);
          // Find end - next Mahadasha or longevity text or end
          const nextDasha = timeline[idx + 1];
          let endIdx = timelinePart.length;
          
          if (nextDasha) {
            const nextPattern = new RegExp(`${nextDasha.dasha_lord} Mahadasha \\(`);
            const nextMatch = timelinePart.substring(startIdx + 10).match(nextPattern);
            if (nextMatch) {
              endIdx = startIdx + 10 + timelinePart.substring(startIdx + 10).indexOf(nextMatch[0]);
            }
          }
          
          // Also check for conclusion markers
          const conclusionMarkers = ['Yeh thi aapki', 'Aapki longevity'];
          for (const marker of conclusionMarkers) {
            const markerIdx = timelinePart.indexOf(marker);
            if (markerIdx > startIdx && markerIdx < endIdx) {
              endIdx = markerIdx;
            }
          }
          
          const extractedText = timelinePart.substring(startIdx, endIdx).trim();
          timelineTexts.push(extractedText);
        } else {
          // Fallback narration - generate user-friendly text
          const organsList = (t.affected_organs || []).slice(0, 3).map(o => 
            typeof o === 'object' ? o.name || String(o) : String(o)
          ).join(', ');
          timelineTexts.push(`${dashaName} Mahadasha (${years}): ${t.health_type || 'Health period'}. ${organsList ? organsList + ' pe dhyan dein.' : ''}`);
        }
      });
    } else {
      intro = fullText;
    }
    
    return { intro, timelineTexts };
  };

  // Load cached data - Updated to use unified story data
  useEffect(() => {
    const loadData = async () => {
      // Get birth details first
      const birthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      if (!birthDetails) {
        console.log('❌ No birth details found for Future Story');
        navigate('/onboarding-new');
        return;
      }
      
      let birthData;
      try {
        birthData = JSON.parse(birthDetails);
      } catch (e) {
        console.error('Error parsing birth details:', e);
        navigate('/onboarding-new');
        return;
      }
      
      const userId = localStorage.getItem('user_id') || localStorage.getItem('session_id') || localStorage.getItem('audio_user_id') || localStorage.getItem('taraanaya_device_id') || `user_${Date.now()}`;
      const deviceId = localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id');
      
      // STEP 1: Try to get unified story data from localStorage first
      let unifiedStoryData = localStorage.getItem('unified_story_data');
      
      // STEP 2: If not in localStorage, try to load from MongoDB (critical on iOS where localStorage quota exceeded)
      if (!unifiedStoryData) {
        console.log('🔍 Trying to load Future Story from MongoDB...');
        try {
          const loadUrl = `${API_URL}/api/unified-story/load/${userId}${deviceId ? `?device_id=${deviceId}` : ''}`;
          const response = await fetch(loadUrl);
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              console.log('✅ Loaded unified story from MongoDB');
              unifiedStoryData = JSON.stringify(result.data);
              try { localStorage.setItem('unified_story_data', unifiedStoryData); } catch(e) { console.log('localStorage save failed (quota):', e.message); }
            }
          }
        } catch (e) {
          console.log('⚠️ MongoDB load error:', e.message);
        }
      }
      
      // STEP 3: If still no data, generate new unified story
      if (!unifiedStoryData) {
        console.log('🔄 Generating new unified story for Future Story...');
        try {
          const generateResponse = await fetch(`${API_URL}/api/unified-story/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: birthData.name || 'User',
              year: birthData.year || parseInt(birthData.dateOfBirth?.split('-')[0]) || 1990,
              month: birthData.month || parseInt(birthData.dateOfBirth?.split('-')[1]) || 1,
              day: birthData.day || parseInt(birthData.dateOfBirth?.split('-')[2]) || 1,
              hour: birthData.hour || parseInt(birthData.timeOfBirth?.split(':')[0]) || 12,
              minute: birthData.minute || parseInt(birthData.timeOfBirth?.split(':')[1]) || 0,
              latitude: birthData.latitude || 28.6139,
              longitude: birthData.longitude || 77.2090,
              gender: birthData.gender || 'unknown',
              user_id: userId
            })
          });
          
          const generateResult = await generateResponse.json();
          
          if (generateResult.success && generateResult.data) {
            console.log('✅ Generated new unified story for Future Story');
            unifiedStoryData = JSON.stringify(generateResult.data);
            localStorage.setItem('unified_story_data', unifiedStoryData);
            
            // Save to MongoDB
            try {
              await fetch(`${API_URL}/api/unified-story/save-story`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: userId, story_data: generateResult.data })
              });
            } catch (saveErr) { console.log('Save error:', saveErr); }
          }
        } catch (genErr) {
          console.error('Error generating unified story:', genErr);
        }
      }
      
      // STEP 4: Parse and process the data
      if (!unifiedStoryData) {
        console.log('❌ Could not load or generate Future Story data');
        navigate('/onboarding-new');
        return;
      }
      
      try {
        const data = JSON.parse(unifiedStoryData);
        setPersonName(data.user_info?.name || 'User');
        
        // Get future mahadashas (current + future)
        let futureMds = data.future_story?.mahadashas || 
                         data.all_mahadashas?.filter(m => m.status === 'current' || m.status === 'future') || [];
        
        // If futureMds is empty, try more sources
        if (futureMds.length === 0 && data.all_mahadashas?.length > 0) {
          // all_mahadashas exists but none matched 'current'/'future' — use year-based filtering
          const currentYear = new Date().getFullYear();
          futureMds = data.all_mahadashas.filter(m => {
            const yearParts = m.years?.split('-') || [];
            const endYear = parseInt(yearParts[1]) || 0;
            return endYear >= currentYear;
          });
          console.log(`✅ Year-filtered ${futureMds.length} future mahadashas from ${data.all_mahadashas.length} total`);
        }
        
        // If still empty, try future_mahadashas field
        if (futureMds.length === 0 && data.future_mahadashas?.length > 0) {
          futureMds = data.future_mahadashas;
          console.log(`✅ Using future_mahadashas: ${futureMds.length}`);
        }
        
        // Last resort: fetch from server
        if (futureMds.length === 0) {
          const cacheKey = localStorage.getItem('story_cache_key') || data.cache_key;
          if (cacheKey) {
            try {
              const resp = await fetch(`${API_URL}/api/unified-story/status/${cacheKey}?device_id=${encodeURIComponent(localStorage.getItem("taraanaya_device_id") || localStorage.getItem("device_id") || "")}`);
              if (resp.ok) {
                const serverResult = await resp.json();
                const serverMds = serverResult?.data?.all_mahadashas || serverResult?.data?.future_story?.mahadashas || [];
                if (serverMds.length > 0) {
                  const currentYear = new Date().getFullYear();
                  futureMds = serverMds.filter(m => {
                    const yearParts = m.years?.split('-') || [];
                    const endYear = parseInt(yearParts[1]) || 0;
                    return endYear >= currentYear || m.status === 'current' || m.status === 'future';
                  });
                  console.log(`✅ Server-fetched ${futureMds.length} future mahadashas`);
                }
              }
            } catch(e) { console.log('Mahadasha server fetch error:', e.message); }
          }
        }
        
        // Get future story intro and timeline
        const futureIntro = data.future_story?.intro || {};
        const futureTimeline = data.future_story?.timeline_narrations || {};
        
        // Set intro text (now includes all 8 domains overview)
        const introForLang = futureIntro[selectedLanguage] || futureIntro.hinglish || 
          `Namaste ${data.user_info?.name || 'User'}! Ab aapke future ki baat karte hain.`;
        setIntroText(introForLang);
        
        // Set full narration object
        setFullNarration(futureIntro);
        
        // Set timeline narrations
        const timelineForLang = futureTimeline[selectedLanguage] || futureTimeline.hinglish || [];
        setTimelineNarrations(timelineForLang);
        
        // Build timeline predictions with all 8 domain info
        const predictions = futureMds.map((md, idx) => {
          const domainPredictions = md.domain_predictions || {};
          
          return {
            year: md.years || '',
            dasha_lord: md.lord,
            zodiac: PLANET_ICONS[md.lord] || '☉',
            color: PLANET_COLORS[md.lord] || md.color || '#edc161',
            risk_level: 'MODERATE',
            is_current: md.status === 'current',
            health_type: domainPredictions.health?.summary || '',
            career_type: domainPredictions.career?.summary || '',
            wealth_type: domainPredictions.wealth?.summary || '',
            travel_type: domainPredictions.travel?.summary || '',
            family_type: domainPredictions.family?.summary || '',
            marriage_type: domainPredictions.marriage?.summary || '',
            affected_organs: domainPredictions.health?.affected_organs || [],
            domain_predictions: domainPredictions,
            astroElements: PLANET_ASTRO_ELEMENTS[md.lord] || [],
            narration: timelineForLang[idx] || `${md.lord} Mahadasha (${md.years})`
          };
        });
        
        setYearlyPredictions(predictions);
        
        // Show UI immediately - don't block on any audio operations
        setIsLoading(false);
        
        // Load audio in background silently
        loadAudioFromMongoDB(userId).catch(e => {
          console.log('Audio will be generated on demand');
        });
        
      } catch (err) {
        console.error('Error parsing unified data:', err);
        // Fallback to old health_prediction_data
        const cachedData = localStorage.getItem('health_prediction_data');
        if (cachedData) {
          try {
            const data = JSON.parse(cachedData);
            setPersonName(data.name || 'User');
            setFullNarration(data.narration || {});
            const { intro, timelineTexts } = parseNarration(data.narration, data.timeline || []);
            setIntroText(intro);
            setTimelineNarrations(timelineTexts);
            
            const predictions = (data.timeline || []).map((t, idx) => ({
              year: t.years || '',
              dasha_lord: t.dasha_lord,
              zodiac: PLANET_ICONS[t.dasha_lord] || '☉',
              color: PLANET_COLORS[t.dasha_lord] || '#edc161',
              risk_level: t.risk_level || 'LOW',
              is_current: t.is_current || false,
              health_type: t.health_type || '',
              affected_organs: t.affected_organs || [],
              astroElements: PLANET_ASTRO_ELEMENTS[t.dasha_lord] || [],
              narration: timelineTexts[idx] || `${t.dasha_lord} Mahadasha (${t.years})`
            }));
            
            setYearlyPredictions(predictions);
            setIsLoading(false);
          } catch (fallbackErr) {
            navigate('/onboarding-new');
          }
        } else {
          navigate('/onboarding-new');
        }
      }
    };
    
    loadData();
  }, [navigate, selectedLanguage]);

  // Load audio from IndexedDB (client-side cache) - $0 SOLUTION
  const loadAudioFromLocalCache = async (userId) => {
    try {
      if (!userId) return;
      
      console.log('📥 Loading audio from local device cache (IndexedDB)...');
      setLoadingMessage('Loading saved audio...');
      
      // Check what's cached locally
      const cacheStats = await LocalAudioCache.getUserAudioCache(userId);
      console.log(`📦 Local cache: ${cacheStats.count} items, ${cacheStats.totalSizeMB} MB`);
      
      if (cacheStats.count > 0) {
        let loadedItems = 0;
        
        // Load intro audio from local cache
        for (const lang of ['hinglish', 'english', 'hindi']) {
          const introAudio = await LocalAudioCache.getAudioLocal(userId, 'future_intro', lang);
          if (introAudio) {
            try {
              const blob = await (await fetch(`data:audio/mp3;base64,${introAudio}`)).blob();
              audioCache.current.intro[lang] = URL.createObjectURL(blob);
              loadedItems++;
            } catch (e) {
              console.log(`Error loading cached intro ${lang}:`, e.message);
            }
          }
        }
        
        // Load timeline audio from local cache
        for (const lang of ['hinglish', 'english', 'hindi']) {
          audioCache.current[lang] = {};
          for (let idx = 0; idx < 20; idx++) {
            const timelineAudio = await LocalAudioCache.getAudioLocal(userId, 'future_timeline', `${lang}_${idx}`);
            if (timelineAudio) {
              try {
                const blob = await (await fetch(`data:audio/mp3;base64,${timelineAudio}`)).blob();
                audioCache.current[lang][idx] = URL.createObjectURL(blob);
                loadedItems++;
              } catch (e) {
                console.log(`Error loading cached timeline ${lang}/${idx}:`, e.message);
              }
            }
          }
        }
        
        if (loadedItems > 0) {
          console.log(`✅ Loaded ${loadedItems} audio files from local cache`);
          setLoadingMessage('Audio ready');
          return true;
        }
      }
      
      console.log('⚠️ No local audio cache - will generate on demand');
      setLoadingMessage('Audio will generate during playback (first time only)');
      return false;
    } catch (e) {
      console.log('Local cache error:', e.message);
      return false;
    }
  };

  // Generate and cache audio on-demand (saves to IndexedDB)
  const generateAndCacheAudio = async (text, language, cacheType, cacheKey) => {
    try {
      const userId = localStorage.getItem('session_id') || localStorage.getItem('user_id');
      
      // First check local cache
      const cached = await LocalAudioCache.getAudioLocal(userId, cacheType, `${language}_${cacheKey}`);
      if (cached) {
        const blob = await (await fetch(`data:audio/mp3;base64,${cached}`)).blob();
        return URL.createObjectURL(blob);
      }
      
      // Generate on-demand from backend
      console.log(`🎙️ Generating audio: ${cacheType}/${language}/${cacheKey}`);
      const response = await fetch(`${API_URL}/api/domain-tts/generate-on-demand`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          language,
          domain: 'future_story',
          user_id: userId
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.audio_base64) {
          // Save to local cache for next time
          await LocalAudioCache.saveAudioLocal(
            userId, 
            cacheType, 
            `${language}_${cacheKey}`, 
            result.audio_base64,
            { generated_at: new Date().toISOString() }
          );
          
          // Return blob URL
          const blob = await (await fetch(`data:audio/mp3;base64,${result.audio_base64}`)).blob();
          return URL.createObjectURL(blob);
        }
      }
      
      return null;
    } catch (e) {
      console.error('Audio generation error:', e);
      return null;
    }
  };

  // Legacy: Load audio from MongoDB (fallback)
  const loadAudioFromMongoDB = async (userId) => {
    try {
      if (!userId) return;
      
      // First try local cache
      const localLoaded = await loadAudioFromLocalCache(userId);
      if (localLoaded) return;
      
      console.log('📥 Checking MongoDB for audio (legacy)...');
      setLoadingMessage('Checking for saved audio...');
      
      const response = await fetch(`${API_URL}/api/audio-cache/load/${userId}/future`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          let totalItems = 0;
          let loadedItems = 0;
          
          // Count total items
          for (const lang of ['hinglish', 'english', 'hindi']) {
            if (result.data.intro?.[lang]) totalItems++;
            if (result.data[lang]) totalItems += Object.keys(result.data[lang]).length;
          }
          
          // Load intro audio and save to local cache
          for (const lang of ['hinglish', 'english', 'hindi']) {
            if (result.data.intro?.[lang]) {
              try {
                const blob = await (await fetch(`data:audio/mp3;base64,${result.data.intro[lang]}`)).blob();
                audioCache.current.intro[lang] = URL.createObjectURL(blob);
                
                // Save to local cache
                await LocalAudioCache.saveAudioLocal(userId, 'future_intro', lang, result.data.intro[lang]);
                
                loadedItems++;
                setAudioLoadProgress(Math.round((loadedItems / totalItems) * 100));
              } catch (e) {
                console.log(`Error loading intro audio for ${lang}:`, e.message);
              }
            }
          }
          
          // Load timeline audio and save to local cache
          for (const lang of ['hinglish', 'english', 'hindi']) {
            if (result.data[lang]) {
              audioCache.current[lang] = {};
              for (const idx of Object.keys(result.data[lang])) {
                try {
                  const blob = await (await fetch(`data:audio/mp3;base64,${result.data[lang][idx]}`)).blob();
                  audioCache.current[lang][idx] = URL.createObjectURL(blob);
                  
                  // Save to local cache
                  await LocalAudioCache.saveAudioLocal(userId, 'future_timeline', `${lang}_${idx}`, result.data[lang][idx]);
                  
                  loadedItems++;
                  setAudioLoadProgress(Math.round((loadedItems / totalItems) * 100));
                  setLoadingMessage(`Loading narration ${loadedItems}/${totalItems}...`);
                } catch (e) {
                  console.log(`Error loading timeline audio ${lang}/${idx}:`, e.message);
                }
              }
            }
          }
          console.log('✅ Audio loaded and saved to local cache');
        } else {
          console.log('⚠️ No audio data available yet - will use TTS on demand');
          setLoadingMessage('Audio will be generated during playback');
        }
      } else {
        console.log('⚠️ Audio cache not found - will generate on demand');
      }
    } catch (e) {
      console.log('Audio load error:', e.message);
      setLoadingMessage('Ready to play');
    }
  };

  // iOS Audio Context unlock - required for WebView audio playback
  // IMPORTANT: This must be called from a direct user tap/click event
  const unlockAudioContext = useCallback(() => {
    console.log('🔓 Unlocking iOS Audio...');
    
    // Step 1: Create and unlock AudioContext (synchronous)
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        const ctx = new AudioContext();
        const buffer = ctx.createBuffer(1, 1, 22050);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
        
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }
        console.log('✅ AudioContext unlocked');
      }
    } catch (e) {
      console.log('AudioContext unlock failed:', e.message);
    }
    
    // Step 2: Prime audio element with silent audio
    const silentAudio = 'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYGq6FAAAAAAAAAAAAAAAAAAAAA';
    const testAudio = new Audio();
    testAudio.volume = 0.01;
    testAudio.src = silentAudio;
    
    testAudio.play()
      .then(() => {
        console.log('✅ Audio element primed');
        testAudio.pause();
      })
      .catch((e) => {
        console.log('⚠️ Audio prime skipped:', e.message);
      });
    
    audioUnlockedRef.current = true;
    console.log('🔓 Audio unlock complete');
  }, []);

  // Handle sage click - Start Story
  const handleSageClick = () => {
    if (currentPhase !== 'ready' || yearlyPredictions.length === 0) return;
    
    console.log('🔘 Sage clicked, isIOS:', isIOS, 'isIOSWebView:', isIOSWebView);
    
    // CRITICAL: Unlock iOS audio on first user tap
    unlockAudioContext();
    
    setCurrentPhase('intro');
    setShowSkipButton(true);
    setCurrentNarrationText(introText);
    playIntroNarration();
  };

  // Play intro narration (45-60 seconds)
  const playIntroNarration = async () => {
    setIsSpeaking(true);
    isPlayingRef.current = true;
    
    // Use audio for selected language (fallback to hinglish)
    let audioUrl = audioCache.current.intro?.[selectedLanguage] || audioCache.current.intro?.hinglish;
    const estimatedDuration = 45000; // 45 seconds for intro
    
    // Start dynamic text reveal
    startTextReveal(introText, estimatedDuration);
    
    // If no cached audio, try to generate on-demand
    if (!audioUrl && introText) {
      console.log('📢 No cached intro audio, generating on-demand...');
      try {
        const response = await fetch(`${API_URL}/api/tts/edge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: introText.substring(0, 500), language: selectedLanguage })
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.audio) {
            audioUrl = `data:audio/mp3;base64,${result.audio}`;
            console.log('✅ On-demand intro audio generated');
          }
        }
      } catch (e) {
        console.log('On-demand TTS failed:', e.message);
      }
    }
    
    if (audioUrl) {
      try {
        // iOS-compatible audio setup
        const audio = new Audio();
        audio.setAttribute('playsinline', 'true');
        audio.setAttribute('webkit-playsinline', 'true');
        audio.setAttribute('x-webkit-airplay', 'allow');
        audio.preload = 'auto';
        audio.volume = 1.0;
        audio.src = audioUrl;
        audioRef.current = audio;
        
        // Get actual duration when available
        audio.addEventListener('loadedmetadata', () => {
          console.log('🎵 Intro audio metadata loaded, duration:', audio.duration);
          if (audio.duration && audio.duration > 0) {
            // Restart text reveal with actual duration
            startTextReveal(introText, audio.duration * 1000);
          }
        });
        
        // For iOS, explicitly load before playing
        if (isIOS) {
          audio.load();
        }
        
        await new Promise((resolve, reject) => {
          audio.onended = () => {
            console.log('🎵 Intro audio ended');
            resolve();
          };
          audio.onerror = (e) => {
            console.error('🔴 Intro audio error:', e.target?.error?.message || e);
            reject(e);
          };
          
          // Small delay for iOS
          setTimeout(() => {
            audio.play()
              .then(() => console.log('✅ Intro audio playing'))
              .catch(reject);
          }, isIOS ? 100 : 0);
        });
      } catch (e) {
        console.log('Intro audio failed, using text only:', e.message);
        // Wait for reading time if audio fails
        await new Promise(r => setTimeout(r, 15000));
      }
    } else {
      // No audio - wait for reading time (15 seconds)
      console.log('⏭️ No audio available, showing text for 15 seconds');
      await new Promise(r => setTimeout(r, 15000));
    }
    
    if (!isPlayingRef.current) return;
    
    stopTextReveal();
    setDisplayedText(introText); // Ensure full text is shown
    setIsSpeaking(false);
    
    // Smooth transition to timeline
    await new Promise(r => setTimeout(r, 1500));
    
    if (!isPlayingRef.current) return;
    
    // Start timeline
    setCurrentPhase('timeline');
    await playTimelineNarrations();
  };

  // Play timeline narrations (one by one) - with on-demand TTS like Past Story
  const playTimelineNarrations = async () => {
    // PRE-FETCH: Generate all timeline audio in parallel before starting playback
    const uncachedNarrations = [];
    for (let idx = 0; idx < yearlyPredictions.length; idx++) {
      const narrationText = timelineNarrations[idx] || yearlyPredictions[idx].narration;
      if (narrationText && !audioCache.current[selectedLanguage]?.[idx]) {
        uncachedNarrations.push({ idx, text: narrationText });
      }
    }
    
    if (uncachedNarrations.length > 0) {
      console.log(`🎙️ Pre-fetching ${uncachedNarrations.length} timeline narrations...`);
      // Fire parallel requests in batches of 4
      const BATCH_SIZE = 4;
      for (let b = 0; b < uncachedNarrations.length; b += BATCH_SIZE) {
        const batch = uncachedNarrations.slice(b, b + BATCH_SIZE);
        await Promise.all(batch.map(async ({ idx, text }) => {
          try {
            const response = await fetch(`${API_URL}/api/tts/edge`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ text: text.substring(0, 500), language: selectedLanguage })
            });
            if (response.ok) {
              const result = await response.json();
              if (result.success && result.audio) {
                if (!audioCache.current[selectedLanguage]) audioCache.current[selectedLanguage] = {};
                audioCache.current[selectedLanguage][idx] = `data:audio/mp3;base64,${result.audio}`;
              }
            }
          } catch (e) { /* ignore pre-fetch errors */ }
        }));
        if (!isPlayingRef.current) return;
      }
      console.log(`✅ Timeline audio pre-fetch complete`);
    }
    
    for (let idx = 0; idx < yearlyPredictions.length; idx++) {
      if (!isPlayingRef.current) return;
      
      const narrationText = timelineNarrations[idx] || yearlyPredictions[idx].narration;
      const estimatedDuration = 15000; // 15 seconds per period
      
      setSelectedYear(idx);
      setCurrentNarrationText(narrationText);
      setIsSpeaking(true);
      
      // Start dynamic text reveal
      startTextReveal(narrationText, estimatedDuration);
      
      // Use audio for selected language
      let audioUrl = audioCache.current[selectedLanguage]?.[idx] 
        || audioCache.current.hinglish?.[idx];
      
      // If no cached audio, generate on-demand (like Past Story)
      if (!audioUrl && narrationText) {
        console.log(`📢 No cached audio for reel ${idx + 1}, generating on-demand...`);
        try {
          const response = await fetch(`${API_URL}/api/tts/edge`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: narrationText.substring(0, 500), language: selectedLanguage })
          });
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.audio) {
              audioUrl = `data:audio/mp3;base64,${result.audio}`;
              // Cache it for future use
              if (!audioCache.current[selectedLanguage]) {
                audioCache.current[selectedLanguage] = {};
              }
              audioCache.current[selectedLanguage][idx] = audioUrl;
              console.log(`✅ On-demand audio generated for reel ${idx + 1}`);
            }
          }
        } catch (e) {
          console.log('On-demand TTS failed:', e.message);
        }
      }
      
      if (audioUrl) {
        try {
          // iOS-compatible audio setup (same as Past Story)
          const audio = new Audio();
          audio.setAttribute('playsinline', 'true');
          audio.setAttribute('webkit-playsinline', 'true');
          audio.setAttribute('x-webkit-airplay', 'allow');
          audio.preload = 'auto';
          audio.volume = 1.0;
          audio.src = audioUrl;
          audioRef.current = audio;
          
          // Get actual duration when available
          audio.addEventListener('loadedmetadata', () => {
            console.log(`🎵 Reel ${idx + 1} audio loaded, duration:`, audio.duration);
            if (audio.duration && audio.duration > 0) {
              startTextReveal(narrationText, audio.duration * 1000);
            }
          });
          
          // For iOS, explicitly load before playing
          if (isIOS) {
            audio.load();
          }
          
          await new Promise((resolve, reject) => {
            audio.onended = () => {
              console.log(`✅ Reel ${idx + 1} audio ended`);
              resolve();
            };
            audio.onerror = (e) => {
              console.error(`🔴 Reel ${idx + 1} audio error:`, e.target?.error?.message || e);
              reject(e);
            };
            
            // Small delay for iOS
            setTimeout(() => {
              audio.play()
                .then(() => console.log(`▶️ Reel ${idx + 1} audio playing`))
                .catch(reject);
            }, isIOS ? 100 : 0);
          });
        } catch (e) {
          console.log(`Reel ${idx + 1} audio failed, using text only:`, e.message);
          // Wait for reading time if audio fails
          const readTime = Math.max(narrationText.split(/\s+/).length * 200, 8000);
          await new Promise(r => setTimeout(r, readTime));
        }
      } else {
        // No audio - wait for reading time per period
        console.log(`⏭️ No audio for reel ${idx + 1}, showing text`);
        const readTime = Math.max(narrationText.split(/\s+/).length * 200, 8000);
        await new Promise(r => setTimeout(r, readTime));
      }
      
      stopTextReveal();
      setDisplayedText(narrationText); // Ensure full text is shown
      setIsSpeaking(false);
      
      // Smooth transition between periods
      if (idx < yearlyPredictions.length - 1) {
        await new Promise(r => setTimeout(r, 1000));
      }
    }
    
    // Complete
    if (isPlayingRef.current) {
      setCurrentPhase('complete');
      setShowSkipButton(false);
    }
  };

  // Skip story
  const handleSkip = () => {
    isPlayingRef.current = false;
    stopTextReveal();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setCurrentPhase('complete');
    setShowSkipButton(false);
    setIsSpeaking(false);
    setDisplayedText('');
  };

  // Replay story from beginning - auto starts
  const handleReplay = () => {
    // Stop any current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    stopTextReveal();
    isPlayingRef.current = false;
    
    // Reset states
    setSelectedYear(-1);
    setIsSpeaking(false);
    setCurrentNarrationText('');
    setDisplayedText('');
    
    // Auto-start the story after a brief delay
    setTimeout(() => {
      setCurrentPhase('intro');
      setShowSkipButton(true);
      setCurrentNarrationText(introText);
      playIntroNarration();
    }, 300);
  };

  // Handle language change - stop current narration completely and restart in new language
  const handleLanguageChange = (newLang) => {
    if (newLang === selectedLanguage) return;
    
    // IMPORTANT: Stop current audio completely
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    stopTextReveal();
    isPlayingRef.current = false;
    
    // Update language
    setSelectedLanguage(newLang);
    
    // Parse narration for new language
    const langKey = newLang === 'english' ? 'english' : newLang === 'hindi' ? 'hindi' : 'hinglish';
    const newNarration = fullNarration[langKey] || fullNarration.hinglish || '';
    
    // Re-parse for new language - FUTURE markers
    const timelineMarker = newLang === 'hindi' 
      ? 'अब आपकी भविष्य जीवन समयरेखा देखते हैं:' 
      : newLang === 'english' 
        ? 'Now let us look at your future life timeline:' 
        : 'Ab aapki future life timeline dekhte hain:';
    
    const markerIdx = newNarration.indexOf(timelineMarker);
    const newIntro = markerIdx > 0 ? newNarration.substring(0, markerIdx + timelineMarker.length) : newNarration.substring(0, 1500);
    
    // Parse timeline narrations for new language
    const newTimelineTexts = [];
    if (markerIdx > 0) {
      const timelinePart = newNarration.substring(markerIdx + timelineMarker.length);
      yearlyPredictions.forEach((t, idx) => {
        const dashaName = t.dasha_lord;
        const years = t.years || '';
        const pattern = newLang === 'hindi' 
          ? `${dashaName} महादशा \\(${years}\\):` 
          : `${dashaName} Mahadasha \\(${years}\\):`;
        const regex = new RegExp(pattern);
        const match = timelinePart.match(regex);
        if (match) {
          const startIdx = timelinePart.indexOf(match[0]);
          const nextDasha = yearlyPredictions[idx + 1];
          let endIdx = timelinePart.length;
          if (nextDasha) {
            const nextPattern = newLang === 'hindi' 
              ? `${nextDasha.dasha_lord} महादशा` 
              : `${nextDasha.dasha_lord} Mahadasha`;
            const nextIdx = timelinePart.indexOf(nextPattern, startIdx + 10);
            if (nextIdx > 0) endIdx = nextIdx;
          }
          newTimelineTexts.push(timelinePart.substring(startIdx, endIdx).trim());
        } else {
          // Fallback with proper text
          const organs = (t.affected_organs || []).slice(0, 3).map(o => 
            typeof o === 'object' ? o.name || String(o) : String(o)
          ).join(', ');
          newTimelineTexts.push(newLang === 'hindi' 
            ? `${dashaName} महादशा (${years}): ${organs ? organs + ' पर ध्यान दें।' : ''}`
            : newLang === 'english'
              ? `${dashaName} Mahadasha (${years}): ${organs ? 'Pay attention to ' + organs + '.' : ''}`
              : `${dashaName} Mahadasha (${years}): ${organs ? organs + ' pe dhyan dein.' : ''}`
          );
        }
      });
    }
    
    setIntroText(newIntro);
    setTimelineNarrations(newTimelineTexts);
    
    // Reset to ready state so user can tap to start in new language
    setCurrentPhase('ready');
    setSelectedYear(-1);
    setIsSpeaking(false);
    setShowSkipButton(false);
    setCurrentNarrationText('');
    setDisplayedText('');
  };

  const currentPeriod = yearlyPredictions[selectedYear] || {};

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#020712', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
        <motion.div 
          animate={{ rotate: 360 }} 
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          style={{ fontSize: '40px' }}
        >
          ✨
        </motion.div>
        <motion.p 
          animate={{ opacity: [0.5, 1, 0.5] }} 
          transition={{ duration: 1.5, repeat: Infinity }}
          style={{ color: '#edc161', fontSize: '16px', textAlign: 'center' }}
        >
          {loadingMessage}
        </motion.p>
        {audioLoadProgress > 0 && (
          <div style={{ width: '200px', height: '4px', background: 'rgba(237, 193, 97, 0.2)', borderRadius: '2px', overflow: 'hidden' }}>
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${audioLoadProgress}%` }}
              style={{ height: '100%', background: 'linear-gradient(90deg, #edc161, #f5d78e)', borderRadius: '2px' }}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at center, rgba(237, 193, 97, 0.08) 0%, #020712 100%)',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      // Respect iPhone home indicator so bottom-most CTA never clips.
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }}>
      {/* Stars */}
      <div style={{ position: 'fixed', inset: 0, background: 'radial-gradient(2px 2px at 20% 30%, white, transparent), radial-gradient(1px 1px at 60% 70%, white, transparent)', backgroundSize: '100vw 100vh', opacity: 0.3, pointerEvents: 'none' }} />

      {/* Language Pills */}
      <div style={{
        position: 'fixed', top: 'max(12px, env(safe-area-inset-top, 12px))', left: '50%', transform: 'translateX(-50%)',
        display: 'flex', gap: '6px', zIndex: 200,
        background: 'rgba(10, 22, 40, 0.95)', backdropFilter: 'blur(15px)',
        padding: '8px 14px', borderRadius: '22px', border: '1px solid rgba(237, 193, 97, 0.4)'
      }}>
        {[{ key: 'hindi', label: 'हिंदी' }, { key: 'hinglish', label: 'Hinglish' }, { key: 'english', label: 'English' }].map(lang => (
          <button key={lang.key} onClick={() => handleLanguageChange(lang.key)}
            style={{
              padding: '6px 12px', borderRadius: '16px', border: 'none',
              background: selectedLanguage === lang.key ? 'rgba(237, 193, 97, 0.35)' : 'transparent',
              color: selectedLanguage === lang.key ? '#edc161' : '#9CA3AF',
              fontSize: '12px', fontWeight: 600, cursor: 'pointer'
            }}>{lang.label}</button>
        ))}
      </div>

      {/* Title */}
      <div style={{ textAlign: 'center', paddingTop: 'calc(70px + env(safe-area-inset-top, 0px))', paddingBottom: '5px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#edc161', margin: 0, textShadow: '0 0 25px rgba(237, 193, 97, 0.6)' }}>
          Your Future Story
        </h1>
        <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px', fontWeight: 500 }}>
          Health & Longevity
        </p>
      </div>

      {/* Sage Section */}
      <div style={{ textAlign: 'center', padding: '10px 20px', position: 'relative' }}>
        <div onClick={handleSageClick} style={{ cursor: currentPhase === 'ready' ? 'pointer' : 'default' }}>
          <motion.img src="/sage-final.png" alt="Sage"
            animate={{ 
              y: currentPhase !== 'ready' ? [0, -8, 0] : [0, -4, 0],
              filter: currentPhase !== 'ready' ? `drop-shadow(0 0 40px ${currentPeriod.color || '#edc161'})` : 'drop-shadow(0 0 20px rgba(237, 193, 97, 0.4))'
            }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            style={{ width: '120px', height: 'auto', margin: '0 auto' }}
          />
          {currentPhase === 'ready' && (
            <motion.p animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}
              style={{ fontSize: '13px', color: '#edc161', marginTop: '8px', fontWeight: 600 }}>
              ✨ Tap to Start Your Journey ✨
            </motion.p>
          )}
        </div>

        {/* Skip Button */}
        {showSkipButton && (
          <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={handleSkip}
            style={{
              position: 'absolute', top: '10px', right: '20px',
              padding: '8px 16px', fontSize: '12px', fontWeight: 600,
              background: 'rgba(237, 193, 97, 0.15)', border: '1px solid rgba(237, 193, 97, 0.4)',
              borderRadius: '20px', color: '#edc161', cursor: 'pointer'
            }}>Skip →</motion.button>
        )}
      </div>

      {/* Narration Box */}
      <AnimatePresence>
        {(currentPhase === 'intro' || currentPhase === 'timeline') && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0 }}
            style={{ margin: '0 auto', padding: '0 15px', maxWidth: '100%', width: '100%' }}
          >
            {/* Bubble tail */}
            <div style={{
              width: 0, height: 0, margin: '0 auto',
              borderLeft: '12px solid transparent', borderRight: '12px solid transparent',
              borderBottom: `12px solid ${currentPhase === 'intro' ? 'rgba(237, 193, 97, 0.3)' : (currentPeriod.color + '40') || 'rgba(237, 193, 97, 0.3)'}`
            }} />
            
            {/* Narration content with scroll */}
            <motion.div
              key={currentPhase === 'intro' ? 'intro' : selectedYear}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              style={{
                padding: '14px 16px',
                background: `linear-gradient(135deg, ${currentPhase === 'intro' ? 'rgba(237, 193, 97, 0.15)' : (currentPeriod.color + '20') || 'rgba(237, 193, 97, 0.15)'}, transparent)`,
                backdropFilter: 'blur(15px)', borderRadius: '16px',
                border: `1.5px solid ${currentPhase === 'intro' ? 'rgba(237, 193, 97, 0.4)' : (currentPeriod.color + '60') || 'rgba(237, 193, 97, 0.4)'}`,
                boxShadow: `0 4px 20px ${currentPhase === 'intro' ? 'rgba(237, 193, 97, 0.2)' : (currentPeriod.color + '30') || 'rgba(237, 193, 97, 0.2)'}`,
                maxHeight: '150px',
                overflowY: 'auto'
              }}
            >
              <p style={{
                fontSize: '13px', lineHeight: '1.7', color: '#E8E8E8', textAlign: 'left', margin: 0, fontWeight: 500
              }}>
                {displayedText || currentNarrationText}
              </p>
            </motion.div>

            {/* Period indicator for timeline phase */}
            {currentPhase === 'timeline' && selectedYear >= 0 && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  textAlign: 'center', marginTop: '10px',
                  fontSize: '16px', fontWeight: 700, color: currentPeriod.color || '#edc161',
                  textShadow: `0 0 15px ${currentPeriod.color || '#edc161'}`
                }}
              >
                {currentPeriod.zodiac} {currentPeriod.year} • {currentPeriod.dasha_lord}
                {currentPeriod.is_current && ' (CURRENT)'}
              </motion.p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Timeline */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '20px 10px' }}>
        <div style={{ position: 'relative', height: '200px', width: '100%' }}>
          {/* Base line */}
          <div style={{
            position: 'absolute', top: '50%', left: '5%', right: '5%',
            height: '2px', background: 'rgba(237, 193, 97, 0.25)', transform: 'translateY(-50%)'
          }} />

          {/* Progress line */}
          <motion.div
            animate={{
              scaleX: currentPhase === 'timeline' && selectedYear >= 0 ? (selectedYear + 1) / yearlyPredictions.length : 0,
              opacity: currentPhase === 'timeline' ? 1 : 0
            }}
            transition={{ duration: 0.8 }}
            style={{
              position: 'absolute', top: '50%', left: '5%', right: '5%',
              height: '4px', background: 'linear-gradient(90deg, #edc161, #edc161)',
              transformOrigin: 'left', borderRadius: '2px', transform: 'translateY(-50%)',
              boxShadow: '0 0 15px #edc161'
            }}
          />

          {/* Timeline dots */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', padding: '0 8%',
            position: 'absolute', top: 0, left: 0, right: 0, height: '100%', alignItems: 'center'
          }}>
            {yearlyPredictions.map((period, idx) => {
              const isActive = currentPhase === 'timeline' && selectedYear === idx;
              const planetColor = period.color || '#edc161';
              return (
                <div key={idx} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  position: 'relative', zIndex: isActive ? 50 : 10,
                  minWidth: isActive ? '80px' : '40px'
                }}>
                  {/* Planet icon above dot when active */}
                  {isActive && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{
                        position: 'absolute', top: '-35px',
                        fontSize: '24px', color: planetColor,
                        textShadow: `0 0 15px ${planetColor}`
                      }}
                    >
                      {period.zodiac}
                    </motion.div>
                  )}
                  
                  {/* Dot with planet color */}
                  <motion.div
                    animate={{ 
                      width: isActive ? '55px' : '16px', 
                      height: isActive ? '55px' : '16px',
                      borderColor: isActive ? planetColor : '#edc161'
                    }}
                    transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
                    style={{
                      borderRadius: '50%',
                      background: isActive 
                        ? `radial-gradient(circle, ${planetColor}50, ${planetColor}10)` 
                        : '#edc161',
                      border: `2px solid ${isActive ? planetColor : '#edc161'}`,
                      boxShadow: isActive 
                        ? `0 0 25px ${planetColor}, 0 0 50px ${planetColor}40` 
                        : '0 0 8px rgba(237, 193, 97, 0.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: isActive ? '9px' : '0px', fontWeight: 700, color: '#fff',
                      position: 'relative'
                    }}
                  >
                    {isActive && period.dasha_lord?.substring(0, 3)}
                    
                    {/* Pulse */}
                    {isActive && isSpeaking && (
                      <motion.div animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
                        transition={{ duration: 1.2, repeat: Infinity }}
                        style={{ position: 'absolute', inset: '-5px', borderRadius: '50%', border: `2px solid ${planetColor}` }}
                      />
                    )}
                  </motion.div>
                  
                  {/* Year label - compact format */}
                  <span style={{
                    fontSize: '7px', color: isActive ? planetColor : '#edc161', 
                    marginTop: isActive ? '8px' : '4px',
                    fontWeight: 600, textAlign: 'center', lineHeight: '1.1',
                    opacity: currentPhase === 'timeline' && !isActive ? 0.4 : 0.9, 
                    whiteSpace: 'nowrap'
                  }}>
                    {period.year?.split('-').map((y, i) => (
                      <span key={i}>{i > 0 ? '-' : ''}{y.slice(-2)}</span>
                    ))}
                  </span>
                  
                  {/* Orbiting icons with planet colors */}
                  <AnimatePresence>
                    {isActive && period.astroElements?.map((el, i) => {
                      const sizes = { xlarge: 26, large: 22, medium: 18, small: 14 };
                      const angle = (Math.PI * 2 / period.astroElements.length) * i - Math.PI / 2;
                      const r = 42;
                      return (
                        <motion.div key={i}
                          initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
                          animate={{ 
                            opacity: 1, scale: 1, 
                            x: Math.cos(angle) * r, 
                            y: Math.sin(angle) * r 
                          }}
                          exit={{ opacity: 0, scale: 0 }}
                          transition={{ delay: i * 0.1, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
                          style={{
                            position: 'absolute',
                            width: sizes[el.size] || 18, height: sizes[el.size] || 18,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${el.color}70, ${el.color}30)`,
                            border: `1.5px solid ${el.color}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: `0 2px 10px ${el.color}60`,
                            fontSize: (sizes[el.size] || 18) * 0.5, color: '#fff'
                          }}
                        >{el.symbol}</motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Continue and Replay buttons */}
      {currentPhase === 'complete' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          style={{ textAlign: 'center', padding: '20px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={handleReplay}
            style={{
              padding: '14px 24px', fontSize: '14px', fontWeight: 700,
              background: 'transparent',
              border: '2px solid #edc161', borderRadius: '25px', color: '#edc161', cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(237, 193, 97, 0.2)'
            }}>Replay Story</button>
          <button onClick={() => navigate('/dashboard')}
            style={{
              padding: '14px 30px', fontSize: '14px', fontWeight: 700,
              background: 'linear-gradient(135deg, #edc161, #d4a84b)',
              border: 'none', borderRadius: '25px', color: '#020712', cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(237, 193, 97, 0.4)'
            }}>Continue →</button>
        </motion.div>
      )}
    </div>
  );
};

export default FutureStoryRevolutionary;
